import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "crluys_admin_token";

export const apiClient = axios.create({
    baseURL: API,
});

apiClient.interceptors.request.use((config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

export const tokenStore = {
    get: () => localStorage.getItem(TOKEN_KEY),
    set: (t) => localStorage.setItem(TOKEN_KEY, t),
    clear: () => localStorage.removeItem(TOKEN_KEY),
};

export const SMART_INVESTING_EXTERNAL_URL = "https://wealth-unlock-3.emergent.host/";
export const SMART_INVESTING_FLAGSHIP_SLUG = "keystone-investors-club";

// Official affiliate checkout URL — used by explicit purchase-intent CTAs
// (e.g. "Enter The Vault" yellow button) so ready-to-buy users skip the
// Keystone landing soft funnel and go straight to digistore24 checkout.
export const KEYSTONE_AFFILIATE_URL = "https://www.digistore24.com/link/34erje275mmv/";

// Skin Reset flagship — same pattern as Keystone. The product card routes
// to /product/complete-skin-reset-system, and ProductPage.jsx replaces the
// window to the dedicated owned landing page. Affiliate URLs inside that
// landing page continue to send visitors to the Digistore24 checkout.
export const SKIN_RESET_EXTERNAL_URL = "https://homestead-health.emergent.host";
export const SKIN_RESET_FLAGSHIP_SLUG = "complete-skin-reset-system";

// Home Doctor flagship — same pattern as Skin Reset. Every Home Doctor
// entry point (product card, image, title, CTA) routes to
// /product/emergency-home-doctor, and ProductPage.jsx replaces the window
// to the dedicated owned landing page. Affiliate CTAs on that landing page
// forward visitors to the vendor.
export const HOME_DOCTOR_EXTERNAL_URL = "https://home-doctor-guide.emergent.host/home-doctor";
export const HOME_DOCTOR_FLAGSHIP_SLUG = "emergency-home-doctor";

// Products using the final supplied landscape artwork — always shown uncropped.
export const FULL_ARTWORK_SLUGS = [
    "the-billionaire-brainwave",
    "ultimate-budget-planner",
    "no-grid-survival-projects",
    "neuroprime",
    "ultimate-energizer-guide",
    "his-secret-obsession",
    "lean-bliss",
    "mega-fitness-bundle",
    "tonic-greens",
    "encyclopedia-of-power-foods",
    "12-weeks-ketogenic-meal-plans",
    "ultimate-off-grid-generator",
    "lumi-water-bottle",
    "self-sufficient-backyard",
    "the-genius-wave",
];

// Categories whose product collection is shown strictly by display order.
export const ORDERED_COLLECTIONS = ["self-sufficiency"];

export function isValidHttpUrl(value) {
    if (!value || typeof value !== "string") return false;
    const trimmed = value.trim();
    if (!trimmed) return false;
    try {
        const u = new URL(trimmed);
        return u.protocol === "http:" || u.protocol === "https:";
    } catch {
        return false;
    }
}

export const CATEGORIES = [
    {
        slug: "smart-investing",
        number: "01",
        name: "Smart Investing",
        short: "Smart Investing",
        tagline: "Build wealth. Secure your future.",
        description:
            "Curated tools, courses & strategies for financial freedom — from investing fundamentals to advanced wealth-building.",
        icon: "TrendingUp",
        accent: "from-amber-300/40 to-yellow-600/10",
        external_url: SMART_INVESTING_EXTERNAL_URL,
        featured_primary: true,
    },
    {
        slug: "health-wellness",
        number: "02",
        name: "Health Home Doctor & Wellness",
        short: "Health & Wellness",
        tagline: "Your body. Your sanctuary.",
        description:
            "Premium wellness resources and at-home health solutions for vitality, longevity, and inner balance.",
        icon: "HeartPulse",
        accent: "from-rose-300/30 to-amber-700/10",
    },
    {
        slug: "fitness-nutrition",
        number: "03",
        name: "Fitness & Nutrition",
        short: "Fitness & Nutrition",
        tagline: "Sculpt strength. Nourish life.",
        description:
            "Programs, meal plans and supplements engineered to elevate your physique, performance and energy.",
        icon: "Dumbbell",
        accent: "from-emerald-300/30 to-amber-700/10",
    },
    {
        slug: "self-sufficiency",
        number: "04",
        name: "Self Sufficiency",
        short: "Self Sufficiency",
        tagline: "Independent living. By design.",
        description:
            "Off-grid energy, food preservation, and survival skills for a resilient, self-reliant lifestyle.",
        icon: "Leaf",
        accent: "from-lime-300/30 to-amber-700/10",
    },
    {
        slug: "mindset-balance",
        number: "05",
        name: "Mindset & Balance",
        short: "Mindset & Balance",
        tagline: "Master your inner world.",
        description:
            "Mental clarity, manifestation programs and emotional balance for a centered, intentional life.",
        icon: "Brain",
        accent: "from-violet-300/30 to-amber-700/10",
    },
];

export const CATEGORY_IMAGES = {
    "smart-investing":
        "https://static.prod-images.emergentagent.com/jobs/bf67ecea-fe54-413e-aee9-b3d228eff2a1/images/649ab03e30d1470eb799b19beb6984b87d48fc3da081a3c3dc954711304c465a.png",
    "health-wellness":
        "https://static.prod-images.emergentagent.com/jobs/bf67ecea-fe54-413e-aee9-b3d228eff2a1/images/d36f208df3a3d007b5a2ec36b7f548f60369488a7a6d33638d5187476cbae61d.png",
    "fitness-nutrition":
        "https://static.prod-images.emergentagent.com/jobs/bf67ecea-fe54-413e-aee9-b3d228eff2a1/images/9c1a831a26cce4f2bf72b18dd636bdcf9bedfc2f5f67e69d05df8e2104ace1c9.png",
    "self-sufficiency":
        "https://static.prod-images.emergentagent.com/jobs/bf67ecea-fe54-413e-aee9-b3d228eff2a1/images/4137e014cc04c6e520118e218e3a3a5e4e008210319e49218ee3b5e8eed58697.png",
    "mindset-balance":
        "https://static.prod-images.emergentagent.com/jobs/bf67ecea-fe54-413e-aee9-b3d228eff2a1/images/c34970b1d5e06c56297adbadcfecac0a69a2660e3e20c8b279943e60735d1b92.png",
};

export const HERO_IMAGE = "/lifestyle/hero-pool-sunset.webp";

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}
